// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";

interface EventSponsor {
  id: string;
  name: string;
  logo_url: string | null;
  website_url: string | null;
  created_at: string;
}

const EventSponsorsManagement: React.FC = () => {
  const { scopeOrgId, createOrgId, ready } = useAdminOrgScope();
  const [sponsors, setSponsors] = useState<EventSponsor[]>([]);
  const [analytics, setAnalytics] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState<EventSponsor | null>(null);
  
  // Local state for form inputs
  const [localFormData, setLocalFormData] = useState({
    name: "",
    website_url: "",
    logo_url: "",
  });
  
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const normalizeWebsiteUrl = (url: string) => {
    const trimmed = url.trim();
    if (!trimmed) return null;
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };

  useEffect(() => {
    if (!ready) return;
    fetchSponsors();
    fetchAnalytics();
  }, [ready, scopeOrgId]);

  const fetchAnalytics = async () => {
    try {
      // v_sponsor_analytics is already scoped to the org admin's org via RLS.
      const { data, error } = await supabase
        .from("v_sponsor_analytics")
        .select("sponsor_name, impressions, clicks, ctr_percent")
        .order("impressions", { ascending: false });
      if (error) throw error;
      setAnalytics(data || []);
    } catch (error) {
      console.error("Error fetching sponsor analytics:", error);
    }
  };

  const fetchSponsors = async () => {
    try {
      let query = supabase
        .from("sponsors")
        .select("*")
        .order("created_at", { ascending: false });

      // Org admin -> only their sponsors; super admin -> all sponsors.
      if (scopeOrgId) query = query.eq("organization_id", scopeOrgId);

      const { data, error } = await query;

      if (error) throw error;
      setSponsors(data || []);
    } catch (error) {
      console.error("Error fetching sponsors:", error);
      toast.error("Failed to load sponsors");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        setLogoFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          setLogoPreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        toast.error("Please select an image file");
      }
    }
  };

  const uploadLogo = async (file: File): Promise<string | null> => {
    setUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const filePath = `sponsors/sponsor-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("org-logos")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from("org-logos")
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error: any) {
      console.error("Error uploading logo:", error);
      toast.error(error?.message || "Failed to upload logo");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const deleteLogo = async (logoUrl: string) => {
    try {
      // Public URL format: .../object/public/org-logos/sponsors/<file>
      const marker = "/org-logos/";
      const idx = logoUrl.indexOf(marker);
      const path = idx >= 0 ? logoUrl.slice(idx + marker.length) : null;
      if (path) {
        await supabase.storage.from("org-logos").remove([path]);
      }
    } catch (error) {
      console.error("Error deleting logo:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let logoUrl = localFormData.logo_url;

    if (logoFile) {
      const uploadedUrl = await uploadLogo(logoFile);
      if (uploadedUrl) {
        logoUrl = uploadedUrl;
        if (editingSponsor?.logo_url) {
          await deleteLogo(editingSponsor.logo_url);
        }
      }
    }

    try {
      if (editingSponsor) {
        const { error } = await supabase
          .from("sponsors")
          .update({
            name: localFormData.name.trim(),
            website_url: normalizeWebsiteUrl(localFormData.website_url),
            logo_url: logoUrl || null,
          })
          .eq("id", editingSponsor.id);

        if (error) throw error;
        toast.success("Sponsor updated successfully");
        setEditingSponsor(null);
      } else {
        const { error } = await supabase.from("sponsors").insert({
          name: localFormData.name.trim(),
          website_url: normalizeWebsiteUrl(localFormData.website_url),
          logo_url: logoUrl || null,
          organization_id: createOrgId,
        });

        if (error) throw error;
        toast.success("Sponsor created successfully");
        setShowCreateForm(false);
      }

      resetForm();
      fetchSponsors();
    } catch (error: any) {
      console.error("Error saving sponsor:", error);
      toast.error(error?.message || "Failed to save sponsor");
    }
  };

  const handleEdit = (sponsor: EventSponsor) => {
    setShowCreateForm(false);
    setEditingSponsor(sponsor);
    setLocalFormData({
      name: sponsor.name,
      website_url: sponsor.website_url || "",
      logo_url: sponsor.logo_url || "",
    });
    setLogoPreview(sponsor.logo_url);
  };

  const handleDelete = async (id: string, logoUrl: string | null) => {
    if (confirm("Are you sure you want to delete this sponsor?")) {
      try {
        const { error } = await supabase.from("sponsors").delete().eq("id", id);
        if (error) throw error;
        if (logoUrl) await deleteLogo(logoUrl);
        toast.success("Sponsor deleted successfully");
        fetchSponsors();
      } catch (error) {
        console.error("Error deleting sponsor:", error);
        toast.error("Failed to delete sponsor");
      }
    }
  };

  const resetForm = () => {
    setLocalFormData({ name: "", website_url: "", logo_url: "" });
    setLogoFile(null);
    setLogoPreview(null);
    setEditingSponsor(null);
  };

  const handleCancel = () => {
    resetForm();
    setShowCreateForm(false);
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setLocalFormData((prev) => ({ ...prev, logo_url: "" }));
  };

  // Handle local input changes
  const handleInputChange = (field: keyof typeof localFormData, value: string) => {
    setLocalFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading sponsors...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Event Sponsors</h1>
          <p className="text-muted-foreground">Manage sponsors for events</p>
        </div>
        <Button
          onClick={() => {
            setEditingSponsor(null);
            setShowCreateForm(true);
          }}
        >
          <Plus className="w-4 h-4 mr-2" /> Add Sponsor
        </Button>
      </div>

      {(showCreateForm || editingSponsor) && (
        <Card className="bg-gray-50 mb-6">
          <CardHeader>
            <CardTitle>
              {editingSponsor ? "Edit Sponsor" : "Create New Sponsor"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Sponsor Name *</Label>
                <Input
                  id="name"
                  value={localFormData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="website">Website URL</Label>
                <Input
                  id="website"
                  type="text"
                  inputMode="url"
                  value={localFormData.website_url}
                  onChange={(e) => handleInputChange('website_url', e.target.value)}
                  placeholder="https://..."
                />
              </div>

              <div>
                <Label htmlFor="logo">Logo</Label>
                <div className="space-y-2">
                  {logoPreview && (
                    <div className="relative inline-block">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="w-32 h-32 object-contain border rounded"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute -top-2 -right-2"
                        onClick={removeLogo}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  <div>
                    <Input
                      id="logo"
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="cursor-pointer"
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      Upload a logo image (PNG, JPG, etc.)
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" disabled={uploading}>
                  {uploading ? "Uploading..." : editingSponsor ? "Update Sponsor" : "Create Sponsor"}
                </Button>
                <Button type="button" variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Sponsors ({sponsors.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {sponsors.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No sponsors found. Create your first sponsor to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Logo</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Website</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sponsors.map((sponsor) => (
                  <TableRow key={sponsor.id}>
                    <TableCell>
                      {sponsor.logo_url ? (
                        <img
                          src={sponsor.logo_url}
                          alt={`${sponsor.name} logo`}
                          className="w-12 h-12 object-contain"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                          No Logo
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{sponsor.name}</TableCell>
                    <TableCell>
                      {sponsor.website_url ? (
                        <a
                          href={sponsor.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          Visit Website
                        </a>
                      ) : (
                        <span className="text-muted-foreground">No website</span>
                      )}
                    </TableCell>
                    <TableCell>{new Date(sponsor.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(sponsor)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(sponsor.id, sponsor.logo_url)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sponsor Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          {analytics.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No analytics yet. Impressions and clicks will appear here once sponsor logos are viewed.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sponsor</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                  <TableHead className="text-right">Clicks</TableHead>
                  <TableHead className="text-right">CTR%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analytics.map((row, i) => (
                  <TableRow key={row.sponsor_name + i}>
                    <TableCell className="font-medium">{row.sponsor_name}</TableCell>
                    <TableCell className="text-right">{row.impressions ?? 0}</TableCell>
                    <TableCell className="text-right">{row.clicks ?? 0}</TableCell>
                    <TableCell className="text-right">{Number(row.ctr_percent ?? 0).toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EventSponsorsManagement;
